import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import styles from './PersonModal.module.css';

/**
 * Modal for assigning/editing a player on a face.
 *
 * Three phases (mirrors matrix-app's PhotoEditView two-phase flow):
 *
 *  - `search`: single "Search or add player" input + results with team
 *    chips. Click a result → assign + close. Type ≥2 chars and click
 *    "Create new player" → enter create phase with the query split on
 *    first whitespace into first_name + last_name.
 *  - `create`: form (prefix/first/last/suffix/jersey + team) for a brand
 *    new player. Collapsed "+ Add national team" toggle reveals a second
 *    team picker. Back button returns to search with the query preserved.
 *  - `edit`: same form, pre-filled from fetchPerson. Triggered when
 *    openPersonModal was called with a personId (already-tagged face).
 *
 * Save logic is passed via onSave prop — the parent decides whether to
 * POST a new Person or PATCH an existing one based on the phase.
 */
export default function PersonModal({
  isOpen,
  position,
  phase,
  // Search-phase inputs
  query,
  onQueryChange,
  personSuggestions,
  matchHubSuggestions,
  highConfNeighbors,
  selectedFace,
  onSelectSuggestion,
  onSelectMatchHubSuggestion,
  onSelectNeighbor,
  onEnterCreatePhase,
  // Create + edit phase form
  personForm,
  onFormChange,
  // Primary team picker
  teamSearchQuery,
  teamSuggestions,
  selectedTeam,
  onTeamSearchChange,
  onSelectTeam,
  onClearTeam,
  // National-team toggle (create phase)
  showNationalTeamPicker,
  onToggleNationalTeamPicker,
  nationalTeamSearchQuery,
  nationalTeamSuggestions,
  selectedNationalTeam,
  onNationalTeamSearchChange,
  onSelectNationalTeam,
  onClearNationalTeam,
  // Back from create → search
  onEnterSearchPhase,
  // Save / Cancel
  savingPerson,
  onSave,
  onClose,
  onDragStart,
}) {
  if (!isOpen) return null;

  const inCreatePhase = phase === 'create';
  const inEditPhase = phase === 'edit';
  const inSearchPhase = phase === 'search';

  const titleByPhase = {
    search: 'Assign player',
    create: 'New player',
    edit: 'Edit player',
  };

  // Save button enabled only when there's something to save.
  const formNameLength = (personForm.first_name || '').trim().length
    + (personForm.last_name || '').trim().length;
  const canSave = !savingPerson && (inEditPhase || (inCreatePhase && formNameLength > 0));

  return (
    <div className={styles.backdrop} onClick={onClose}>
      <div
        className={styles.card}
        onClick={(e) => e.stopPropagation()}
        style={{ transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px))` }}
      >
        <div className={styles.header} onMouseDown={onDragStart}>
          {inCreatePhase ? (
            <button
              type="button"
              className={styles.backBtn}
              onClick={onEnterSearchPhase}
              title="Back to search"
            >
              <ArrowLeftIcon className={styles.icon} />
            </button>
          ) : null}
          <h4>{titleByPhase[phase] || 'Assign player'}</h4>
          <button className={styles.closeBtn} onClick={onClose}>
            <XMarkIcon className={styles.icon} />
          </button>
        </div>

        <div className={styles.body}>
          {inSearchPhase && (
            <SearchPhase
              query={query}
              onQueryChange={onQueryChange}
              suggestions={personSuggestions}
              matchHubSuggestions={matchHubSuggestions}
              onSelect={onSelectSuggestion}
              onSelectMatchHub={onSelectMatchHubSuggestion}
              onEnterCreate={onEnterCreatePhase}
              selectedFace={selectedFace}
              highConfNeighbors={highConfNeighbors}
              onSelectNeighbor={onSelectNeighbor}
            />
          )}

          {(inCreatePhase || inEditPhase) && (
            <>
              <PersonFormFields
                personForm={personForm}
                onFormChange={onFormChange}
                teamSearchQuery={teamSearchQuery}
                teamSuggestions={teamSuggestions}
                selectedTeam={selectedTeam}
                onTeamSearchChange={onTeamSearchChange}
                onSelectTeam={onSelectTeam}
                onClearTeam={onClearTeam}
              />

              {/* National-team toggle only on create — edit users manage a
                  second membership via Manage > Persons or the P6 bulk
                  modal, not inline. */}
              {inCreatePhase && (
                <NationalTeamSection
                  open={showNationalTeamPicker}
                  onToggle={onToggleNationalTeamPicker}
                  query={nationalTeamSearchQuery}
                  suggestions={nationalTeamSuggestions}
                  selectedTeam={selectedNationalTeam}
                  onQueryChange={onNationalTeamSearchChange}
                  onSelect={onSelectNationalTeam}
                  onClear={onClearNationalTeam}
                />
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            className={styles.saveBtn}
            disabled={!canSave}
            onClick={onSave}
          >
            {savingPerson ? 'Saving...' : inSearchPhase ? 'Done' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Phase 1 — search bar + results + "Create new player" trigger + similar faces.
 */
function SearchPhase({
  query,
  onQueryChange,
  suggestions,
  matchHubSuggestions = [],
  onSelect,
  onSelectMatchHub,
  onEnterCreate,
  selectedFace,
  highConfNeighbors,
  onSelectNeighbor,
}) {
  const showCreateRow = (query || '').trim().length >= 2;
  return (
    <>
      <div className={styles.searchField}>
        <label>Search or add player</label>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Type a name…"
          value={query}
          autoFocus
          autoComplete="off"
          onChange={(e) => onQueryChange(e.target.value)}
        />
      </div>

      {suggestions.length > 0 && (
        <>
          <div className={styles.groupLabel}>In Matrix</div>
          <SuggestionsList suggestions={suggestions} onSelect={onSelect} />
        </>
      )}

      {matchHubSuggestions.length > 0 && (
        <>
          <div className={styles.groupLabel}>From MatchHub</div>
          <ul className={styles.suggestionsList}>
            {matchHubSuggestions.slice(0, 10).map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={styles.suggestionRow}
                  onClick={() => onSelectMatchHub?.(p)}
                >
                  <span>{p.name}</span>
                  <span className={styles.suggestionMeta}>
                    {[p.position, p.birth_date].filter(Boolean).join(' · ')}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {showCreateRow && (
        <button
          type="button"
          className={styles.createNewRow}
          onClick={onEnterCreate}
        >
          + Create new player &ldquo;{query.trim()}&rdquo;
        </button>
      )}

      {selectedFace && (
        <SimilarFaces neighbors={highConfNeighbors} onPick={onSelectNeighbor} />
      )}
    </>
  );
}

/**
 * Shared form fields for create + edit phases.
 */
function PersonFormFields({
  personForm,
  onFormChange,
  teamSearchQuery,
  teamSuggestions,
  selectedTeam,
  onTeamSearchChange,
  onSelectTeam,
  onClearTeam,
}) {
  const handleChange = (field) => (e) => {
    onFormChange({ ...personForm, [field]: e.target.value });
  };

  return (
    <div className={styles.formGrid}>
      <div className={styles.formField}>
        <label>First name</label>
        <input
          placeholder="First name"
          value={personForm.first_name}
          autoComplete="off"
          autoFocus
          onChange={handleChange('first_name')}
        />
      </div>
      <div className={styles.formField}>
        <label>Last name</label>
        <input
          placeholder="Last name"
          value={personForm.last_name}
          autoComplete="off"
          onChange={handleChange('last_name')}
        />
      </div>
      <div className={styles.formField}>
        <label>Team</label>
        <TeamSearchInput
          query={teamSearchQuery}
          suggestions={teamSuggestions}
          selectedTeam={selectedTeam}
          onQueryChange={onTeamSearchChange}
          onSelect={onSelectTeam}
          onClear={onClearTeam}
          placeholder="Search teams..."
        />
      </div>
      <div className={`${styles.formField} ${styles.formFieldHalf}`}>
        <label>Jersey #</label>
        <input
          placeholder="#"
          value={personForm.jersey_number}
          inputMode="numeric"
          autoComplete="off"
          onChange={handleChange('jersey_number')}
        />
      </div>
      <div className={`${styles.formField} ${styles.formFieldHalf}`}>
        <label>Prefix</label>
        <input
          placeholder="Coach, Goalkeeper…"
          value={personForm.prefix}
          autoComplete="off"
          onChange={handleChange('prefix')}
        />
      </div>
      <div className={`${styles.formField} ${styles.formFieldHalf}`}>
        <label>Suffix</label>
        <input
          placeholder="Jr., Sr."
          value={personForm.suffix}
          autoComplete="off"
          onChange={handleChange('suffix')}
        />
      </div>
    </div>
  );
}

/**
 * Collapsed "+ Add national team" toggle. When opened, shows a second
 * team search input. Selecting a team here doesn't change the primary
 * team; the parent's save handler addTeamMember()s it as a second
 * membership after the Person is created (P4 caps at 1 CLUB + 1
 * NATIONAL — collision is surfaced as a warning, not a rollback).
 */
function NationalTeamSection({
  open,
  onToggle,
  query,
  suggestions,
  selectedTeam,
  onQueryChange,
  onSelect,
  onClear,
}) {
  return (
    <div className={styles.nationalTeamSection}>
      <button
        type="button"
        className={styles.nationalTeamToggle}
        onClick={onToggle}
      >
        {open ? '− Hide national team' : '+ Add national team'}
      </button>
      {open && (
        <div className={styles.formField}>
          <label>National team</label>
          <TeamSearchInput
            query={query}
            suggestions={suggestions}
            selectedTeam={selectedTeam}
            onQueryChange={onQueryChange}
            onSelect={onSelect}
            onClear={onClear}
            placeholder="Search national teams..."
          />
        </div>
      )}
    </div>
  );
}

/**
 * Searchable team input with autocomplete suggestions.
 */
function TeamSearchInput({
  query,
  suggestions,
  selectedTeam,
  onQueryChange,
  onSelect,
  onClear,
  placeholder = 'Search teams...',
}) {
  return (
    <div className={styles.teamSearchContainer}>
      <div className={styles.teamInputWrapper}>
        <input
          type="text"
          className={styles.teamSearchInput}
          placeholder={placeholder}
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          autoComplete="off"
        />
        {selectedTeam && (
          <button
            type="button"
            className={styles.clearTeamBtn}
            onClick={onClear}
            title="Clear team"
          >
            <XMarkIcon className={styles.clearIcon} />
          </button>
        )}
      </div>
      {suggestions.length > 0 && (
        <div className={styles.teamSuggestionsList}>
          {suggestions.map((team) => (
            <button
              key={team.id}
              type="button"
              className={styles.teamSuggestionItem}
              onClick={() => onSelect(team)}
            >
              <span className={styles.teamName}>{team.name}</span>
              {team.team_type && (
                <span className={styles.teamType}>{team.team_type}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Compact team affiliation chip — green for CLUB, blue for NATIONAL.
 * Capped at 2 chips per person by the P4 membership validator.
 */
function TeamChip({ membership }) {
  const variant =
    membership.team_type === 'national' ? styles.teamChipNational : styles.teamChipClub;
  return (
    <span className={`${styles.teamChip} ${variant}`}>
      {membership.team_short_name || membership.team_name}
    </span>
  );
}

/**
 * List of matching player suggestions.
 */
function SuggestionsList({ suggestions, onSelect }) {
  return (
    <div className={styles.suggestionsContainer}>
      <div className={styles.suggestionsHeader}>
        <span className={styles.suggestionsLabel}>Matching players</span>
        <span className={styles.suggestionsCount}>
          {suggestions.length > 10 ? '10+' : suggestions.length}
        </span>
      </div>
      <div className={styles.suggestionsList}>
        {suggestions.slice(0, 10).map((p) => (
          <button
            key={p.id}
            type="button"
            className={styles.suggestionItem}
            onClick={() => onSelect(p)}
          >
            <span className={styles.suggestionName}>
              {p.display_name || `${p.first_name || ''} ${p.last_name || ''}`.trim()}
            </span>
            {(p.team_memberships || []).length > 0 && (
              <span className={styles.suggestionChips}>
                {p.team_memberships.map((m) => (
                  <TeamChip key={m.id || m.team_id} membership={m} />
                ))}
              </span>
            )}
            {p.jersey_number && (
              <span className={styles.suggestionJersey}>#{p.jersey_number}</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Clickable list of similar persons derived from high-confidence face
 * neighbors (>80% similarity). Each row is a single Person — neighbors
 * are deduped by person_id keeping the closest match. Clicking a row
 * assigns the active face to that person directly.
 *
 * Replaces the prior thumbnail grid, which relied on face thumbnails
 * that Matrix doesn't generate.
 */
function SimilarFaces({ neighbors, onPick }) {
  const rows = (() => {
    const byPerson = new Map();
    for (const n of neighbors || []) {
      const pid = n.person_id || n.person?.id || null;
      if (!pid) continue;
      const name =
        n.person_name ||
        n.person_display ||
        n.person?.display_name ||
        '';
      if (!name) continue;
      const distance = typeof n.distance === 'number' ? n.distance : null;
      const prior = byPerson.get(pid);
      if (!prior || (distance != null && distance < prior.distance)) {
        byPerson.set(pid, { person_id: pid, name, distance });
      }
    }
    return Array.from(byPerson.values()).sort((a, b) => {
      const da = a.distance == null ? Infinity : a.distance;
      const db = b.distance == null ? Infinity : b.distance;
      return da - db;
    });
  })();

  if (rows.length === 0) return null;

  const fmtPct = (d) => {
    if (d == null) return '—';
    const pct = Math.max(0, Math.min(100, (1 - d) * 100));
    return `${Math.round(pct)}%`;
  };

  return (
    <div className={styles.suggestionsContainer} style={{ marginTop: 14 }}>
      <div className={styles.suggestionsHeader}>
        <span className={styles.suggestionsLabel}>Similar persons (&gt;80%)</span>
        <span className={styles.suggestionsCount}>{rows.length}</span>
      </div>
      <div className={styles.suggestionsList}>
        {rows.slice(0, 6).map((r) => (
          <button
            key={r.person_id}
            type="button"
            className={styles.suggestionItem}
            onClick={() => onPick?.(r)}
            disabled={!onPick}
            title="Click to assign this person to the active face"
          >
            <span className={styles.suggestionName}>{r.name}</span>
            <span className={styles.suggestionJersey}>{fmtPct(r.distance)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
